import { z } from 'zod';

/**
 * Client-side mirror of the api-server `partnerCreateSchema`
 * (services/api-server/src/dtos/partner.dto.ts). Kept in sync manually because
 * the DTO is not exported from a shared package. Note: `partner_code` is 3–10
 * UPPERCASE alphanumeric to match the API and the `parties.client_code` check
 * constraint — the ticket's "1–50" range is superseded by the stricter API rule.
 */
export const partnerFormSchema = z.object({
  partner_name: z
    .string()
    .trim()
    .min(1, 'Name is required')
    .max(255, 'Name must be 255 characters or fewer'),
  partner_code: z
    .string()
    .trim()
    .min(3, 'Code must be at least 3 characters')
    .max(10, 'Code must be 10 characters or fewer')
    .regex(/^[A-Z0-9]+$/, 'Code must be uppercase letters (A–Z) and digits only'),
  party_role_id: z
    .number({ required_error: 'Role is required' })
    .int()
    .positive('Role is required'),
  party_status_id: z
    .number({ required_error: 'Status is required' })
    .int()
    .positive('Status is required'),
  metadata: z.record(z.string(), z.unknown()).nullable(),
});

export type PartnerFormValues = z.infer<typeof partnerFormSchema>;

export const flattenZodErrors = (error: z.ZodError): Partial<Record<string, string>> => {
  const result: Partial<Record<string, string>> = {};
  for (const issue of error.issues) {
    const key = issue.path[0];
    if (typeof key === 'string' && !result[key]) result[key] = issue.message;
  }
  return result;
};

export type MetadataParseResult =
  | { ok: true; value: Record<string, unknown> | null }
  | { ok: false; error: string };

/**
 * Parse the raw JSON metadata editor input. Empty input is treated as `null`
 * (clears the column). Valid input must be a JSON object; arrays and scalars are
 * rejected because the column is a keyed metadata bag.
 */
export const parseMetadataInput = (raw: string): MetadataParseResult => {
  const trimmed = raw.trim();
  if (trimmed === '') {
    return { ok: true, value: null };
  }
  let parsed: unknown;
  try {
    parsed = JSON.parse(trimmed);
  } catch {
    return { ok: false, error: 'Invalid JSON' };
  }
  if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) {
    return { ok: false, error: 'Metadata must be a JSON object (e.g. { "key": "value" })' };
  }
  return { ok: true, value: parsed as Record<string, unknown> };
};

/**
 * Pretty-print existing metadata for the editor. Only `null`/`undefined` render
 * as '' (which parseMetadataInput maps back to null). An empty object `{}` is a
 * real, distinct value and is preserved as `{}` so opening Edit and saving
 * without changes doesn't silently rewrite `{}` to null.
 */
export const stringifyMetadata = (metadata: Record<string, unknown> | null | undefined): string => {
  if (metadata == null) return '';
  return JSON.stringify(metadata, null, 2);
};

export const formatPartnerDate = (iso: string): string =>
  new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
